import React, { Suspense, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ArrowRight,
  ChevronRight,
  Eye,
  Menu,
  Search,
  TrendingUp,
  User,
  X,
} from "lucide-react";
import "../../styles/pmV2.css";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";
import { homeV2Asset } from "../../utils/homeV2Assets";
import "./PublicHeader.css";

const LOGO_SRC = homeV2Asset("private-muse-logo.svg");

let preSignupModalPromise = null;
const loadPreSignupModal = () => {
  if (!preSignupModalPromise) {
    preSignupModalPromise = import("../PreSignupModal/PreSignupModal");
  }
  return preSignupModalPromise;
};
const PreSignupModal = React.lazy(loadPreSignupModal);

const NAV_LINKS = [
  { to: "/acompanhantes-virtuais/", label: "Explorar", Icon: Search },
  { to: "/como-funciona/", label: "Como funciona", Icon: ArrowRight },
  { to: "/blog/", label: "Blog", Icon: ChevronRight },
  { to: "/contos-eroticos/", label: "Contos Eróticos", Icon: ChevronRight },
];

const DESKTOP_NAV_LINKS = NAV_LINKS.filter(({ to }) =>
  ["/blog/", "/contos-eroticos/"].includes(to)
);

function normalizePath(path = "/") {
  if (!path || path === "/") return "/";
  return path.replace(/\/+$/, "") || "/";
}

function isActivePath(pathname, target) {
  const current = normalizePath(pathname);
  const normalizedTarget = normalizePath(target);
  if (normalizedTarget === "/") return current === "/";
  return current === normalizedTarget || current.startsWith(`${normalizedTarget}/`);
}

export default function PublicHeader() {
  const location = useLocation();
  const [isPreSignupOpen, setIsPreSignupOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  useEffect(() => {
    setIsDrawerOpen(false);
  }, [location.pathname]);

  useLockBodyScroll(isDrawerOpen);

  const openSignup = () => {
    loadPreSignupModal();
    setIsPreSignupOpen(true);
  };
  const closeSignup = () => setIsPreSignupOpen(false);

  return (
    <>
      <header className="public-header pm-v2-scope">
        <div className="public-header__gold-line" aria-hidden="true" />
        <div className="public-header__inner">
          <Link
            className="public-header__brand"
            to="/"
            aria-label="Private Muse, voltar para a página inicial"
          >
            <img
              className="public-header__brand-logo"
              src={LOGO_SRC}
              alt=""
              width="38"
              height="38"
              loading="eager"
              decoding="async"
            />
            <span className="public-header__brand-divider" aria-hidden="true" />
            <span className="public-header__brand-copy">
              <span className="public-header__brand-title">Private Muse</span>
            </span>
          </Link>

          <nav className="public-header__nav" aria-label="Navegação pública">
            {DESKTOP_NAV_LINKS.map(({ to, label }) => {
              const active = isActivePath(location.pathname, to);
              return (
                <Link
                  key={to}
                  className={`public-header__nav-link${active ? " is-active" : ""}`}
                  to={to}
                  aria-current={active ? "page" : undefined}
                >
                  {label}
                </Link>
              );
            })}
          </nav>

          <div className="public-header__actions">
            <Link className="public-header__login" to="/login">
              <User size={15} aria-hidden="true" />
              <span>Entrar</span>
            </Link>
            <button
              type="button"
              className="public-header__signup"
              onPointerEnter={loadPreSignupModal}
              onFocus={loadPreSignupModal}
              onClick={openSignup}
            >
              Cadastrar
            </button>
            <span className="public-header__action-divider" aria-hidden="true" />
            <button
              type="button"
              className="public-header__menu-button"
              aria-label="Abrir menu"
              aria-expanded={isDrawerOpen}
              aria-controls="public-header-drawer"
              onClick={() => setIsDrawerOpen(true)}
            >
              <Menu size={22} aria-hidden="true" />
            </button>
          </div>
        </div>
        <div className="public-header__base-line" aria-hidden="true" />
      </header>

      <div
        className={`public-header__drawer-backdrop${isDrawerOpen ? " is-open" : ""}`}
        onClick={() => setIsDrawerOpen(false)}
        aria-hidden="true"
      />

      <aside
        id="public-header-drawer"
        className={`public-header__drawer pm-v2-scope${isDrawerOpen ? " is-open" : ""}`}
        aria-hidden={!isDrawerOpen}
      >
        <div className="public-header__drawer-line" aria-hidden="true" />
        <div className="public-header__drawer-top">
          <Link className="public-header__drawer-brand" to="/">
            <img src={LOGO_SRC} alt="" width="26" height="26" loading="lazy" decoding="async" />
            <span>Private Muse</span>
          </Link>
          <button
            type="button"
            className="public-header__drawer-close"
            onClick={() => setIsDrawerOpen(false)}
            aria-label="Fechar menu"
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>

        <nav className="public-header__drawer-nav" aria-label="Menu publico mobile">
          <p>Navegar</p>
          {NAV_LINKS.map(({ to, label, Icon }) => {
            const active = isActivePath(location.pathname, to);
            return (
              <Link
                key={to}
                className={active ? "is-active" : ""}
                to={to}
                aria-current={active ? "page" : undefined}
              >
                <span>
                  <Icon size={16} aria-hidden="true" />
                  {label}
                </span>
                <ChevronRight size={15} aria-hidden="true" />
              </Link>
            );
          })}
        </nav>

        <div className="public-header__drawer-account">
          <p>Já tenho conta</p>
          <Link to="/login">
            <span className="public-header__drawer-icon">
              <User size={16} aria-hidden="true" />
            </span>
            <span>
              <strong>Entrar na minha conta</strong>
              <small>Acesse seu perfil e favoritos</small>
            </span>
            <ChevronRight size={15} aria-hidden="true" />
          </Link>
        </div>

        <div className="public-header__drawer-signup">
          <p>Criar conta gratis</p>
          <div>
            <Link to="/cadastro-acompanhante/">
              <span className="public-header__drawer-icon public-header__drawer-icon--primary">
                <TrendingUp size={17} aria-hidden="true" />
              </span>
              <strong>Quero anunciar</strong>
              <small>Crie seu perfil</small>
            </Link>
            <Link to="/cadastro/?tab=cliente">
              <span className="public-header__drawer-icon public-header__drawer-icon--accent">
                <Eye size={17} aria-hidden="true" />
              </span>
              <strong>Quero explorar</strong>
              <small>Acesse perfis</small>
            </Link>
          </div>
        </div>

        <div className="public-header__drawer-footer">
          Plataforma adulta - +18 - Dados protegidos
        </div>
      </aside>

      {isPreSignupOpen && (
        <Suspense fallback={null}>
          <PreSignupModal isOpen={isPreSignupOpen} onClose={closeSignup} />
        </Suspense>
      )}
    </>
  );
}
